import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DialogFooter } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  mutualFundFormSchema,
  type MutualFundFormSchema,
} from "./mutualFundForm.schema";
import type { MutualFund, MutualFundFormData } from "@/types/mutualFund";
import { format } from "date-fns";
import { useRiskRatings } from "@/hooks/useRiskRatings";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Info } from "lucide-react";
import { useCurrencyList } from "@/hooks/useCurrencyList";

const FUND_CATEGORIES = [
  { value: "equity", label: "Equity" },
  { value: "debt", label: "Debt" },
  { value: "hybrid", label: "Hybrid" },
  { value: "index", label: "Index" },
  { value: "sectoral", label: "Sectoral" },
] as const;

interface MutualFundFormProps {
  mutualFund?: MutualFund | null;
  onSave: (data: MutualFundFormData) => void | Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
}

/**
 * MutualFundForm handles creating and editing mutual fund investment accounts.
 * Captures both base investment fields and mutual-fund-specific fields
 * (NAV, units, cost basis, category, risk rating, benchmark).
 */
export function MutualFundForm({
  mutualFund,
  onSave,
  onCancel,
  isSubmitting = false,
}: MutualFundFormProps) {
  const meta = mutualFund?.mutual_fund_metadata;
  const { data: riskRatings = [], isLoading: riskRatingsLoading } =
    useRiskRatings();
  const {
    data: availableCurrencies,
    isLoading: isLoadingCurrencies,
    error: errorLoadingCurrencies,
  } = useCurrencyList();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting: formIsSubmitting },
  } = useForm<MutualFundFormSchema>({
    resolver: zodResolver(mutualFundFormSchema),
    defaultValues: {
      fund_name: mutualFund?.fund_name ?? "",
      fund_manager: mutualFund?.fund_manager ?? "",
      account_number: mutualFund?.account_number ?? "",
      currency: mutualFund?.currency ?? "USD",
      interest_rate: mutualFund?.interest_rate ?? 0,
      starting_amount: mutualFund?.starting_amount ?? 0,
      notes: mutualFund?.notes ?? "",
      description: mutualFund?.description ?? "",
      fund_category: meta?.fund_category ?? "equity",
      risk_rating: meta?.risk_rating ?? "moderate",
      benchmark_index: meta?.benchmark_index ?? "",
      isin: meta?.isin ?? "",
      units_held: meta?.units_held ?? 0,
      unit_cost_basis: meta?.unit_cost_basis ?? 0,
      current_nav: meta?.current_nav ?? 0,
      nav_date: meta?.nav_date ?? format(new Date(), "yyyy-MM-dd"),
    },
  });

  const isEditing = !!mutualFund;
  const submitting = isSubmitting || formIsSubmitting;

  const onSubmit = (data: MutualFundFormSchema) => {
    onSave(data as MutualFundFormData);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
      {/* ── Fund basics ── */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="fund_name">Fund Name *</Label>
          <Input
            id="fund_name"
            {...register("fund_name")}
            placeholder="e.g. Vanguard 500 Index"
          />
          {errors.fund_name && (
            <p className="text-sm text-destructive">
              {errors.fund_name.message}
            </p>
          )}
        </div>

        <div className="space-y-1">
          <Label htmlFor="fund_manager">Fund Manager *</Label>
          <Input
            id="fund_manager"
            {...register("fund_manager")}
            placeholder="e.g. Vanguard"
          />
          {errors.fund_manager && (
            <p className="text-sm text-destructive">
              {errors.fund_manager.message}
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="account_number">Account / Folio Number *</Label>
          <Input
            id="account_number"
            {...register("account_number")}
            placeholder="e.g. 1234567890"
          />
          {errors.account_number && (
            <p className="text-sm text-destructive">
              {errors.account_number.message}
            </p>
          )}
        </div>

        <div className="space-y-1">
          <Label htmlFor="currency">Currency *</Label>
          <Select
            value={watch("currency")}
            onValueChange={(v) => setValue("currency", v)}>
            <SelectTrigger id="currency">
              <SelectValue placeholder="Select currency" />
            </SelectTrigger>
            {isLoadingCurrencies && "Loading Currencies"}
            {!isLoadingCurrencies && !errorLoadingCurrencies ? (
              <SelectContent>
                {availableCurrencies.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            ) : (
              "Failed to load currencies"
            )}
          </Select>
          {errors.currency && (
            <p className="text-sm text-destructive">
              {errors.currency.message}
            </p>
          )}
        </div>
      </div>

      {/* Starting amount only shown on create */}
      {!isEditing && (
        <div className="space-y-1">
          <Label htmlFor="starting_amount">Starting Amount *</Label>
          <Input
            id="starting_amount"
            type="number"
            step="0.01"
            min="0"
            {...register("starting_amount", { valueAsNumber: true })}
          />
          {errors.starting_amount && (
            <p className="text-sm text-destructive">
              {errors.starting_amount.message}
            </p>
          )}
        </div>
      )}

      <div className="space-y-1">
        <Label htmlFor="interest_rate">Annual Return Rate (%) *</Label>
        <Input
          id="interest_rate"
          type="number"
          step="0.01"
          min="0"
          max="100"
          {...register("interest_rate", { valueAsNumber: true })}
        />
        {errors.interest_rate && (
          <p className="text-sm text-destructive">
            {errors.interest_rate.message}
          </p>
        )}
      </div>

      {/* ── Fund classification ── */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="fund_category">Fund Category *</Label>
          <Select
            value={watch("fund_category")}
            onValueChange={(v) => setValue("fund_category", v as any)}>
            <SelectTrigger id="fund_category">
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              {FUND_CATEGORIES.map((c) => (
                <SelectItem key={c.value} value={c.value}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.fund_category && (
            <p className="text-sm text-destructive">
              {errors.fund_category.message}
            </p>
          )}
        </div>

        <div className="space-y-1">
          <Label htmlFor="risk_rating">Risk Rating *</Label>
          <Select
            value={watch("risk_rating")}
            onValueChange={(v) => setValue("risk_rating", v as any)}
            disabled={riskRatingsLoading}>
            <SelectTrigger id="risk_rating">
              <SelectValue
                placeholder={riskRatingsLoading ? "Loading…" : "Select risk"}
              />
            </SelectTrigger>
            <SelectContent>
              <TooltipProvider>
                {riskRatings.map((r) => (
                  <Tooltip key={r.id}>
                    <TooltipTrigger asChild>
                      <SelectItem value={r.name.toLowerCase()}>
                        <span className="flex items-center gap-1.5">
                          {r.name}
                          {r.description && (
                            <Info
                              className="h-3.5 w-3.5 text-muted-foreground"
                              aria-hidden="true"
                            />
                          )}
                        </span>
                      </SelectItem>
                    </TooltipTrigger>
                    {r.description && (
                      <TooltipContent side="right" className="max-w-56">
                        {r.description}
                      </TooltipContent>
                    )}
                  </Tooltip>
                ))}
              </TooltipProvider>
            </SelectContent>
          </Select>
          {errors.risk_rating && (
            <p className="text-sm text-destructive">
              {errors.risk_rating.message}
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="benchmark_index">Benchmark Index</Label>
          <Input
            id="benchmark_index"
            {...register("benchmark_index")}
            placeholder="e.g. S&P 500, NIFTY 50 (optional)"
          />
        </div>

        <div className="space-y-1">
          <Label htmlFor="isin">ISIN</Label>
          <Input
            id="isin"
            {...register("isin")}
            placeholder="e.g. US0378331005 (optional)"
            maxLength={12}
          />
          {errors.isin && (
            <p className="text-sm text-destructive">{errors.isin.message}</p>
          )}
        </div>
      </div>

      {/* ── NAV & Units ── */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="units_held">Units Held *</Label>
          <Input
            id="units_held"
            type="number"
            step="0.000001"
            min="0"
            {...register("units_held", { valueAsNumber: true })}
          />
          {errors.units_held && (
            <p className="text-sm text-destructive">
              {errors.units_held.message}
            </p>
          )}
        </div>

        <div className="space-y-1">
          <Label htmlFor="unit_cost_basis">Avg. Cost per Unit *</Label>
          <Input
            id="unit_cost_basis"
            type="number"
            step="0.000001"
            min="0"
            {...register("unit_cost_basis", { valueAsNumber: true })}
          />
          {errors.unit_cost_basis && (
            <p className="text-sm text-destructive">
              {errors.unit_cost_basis.message}
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="current_nav">Current NAV per Unit *</Label>
          <Input
            id="current_nav"
            type="number"
            step="0.000001"
            min="0"
            {...register("current_nav", { valueAsNumber: true })}
          />
          {errors.current_nav && (
            <p className="text-sm text-destructive">
              {errors.current_nav.message}
            </p>
          )}
        </div>

        <div className="space-y-1">
          <Label htmlFor="nav_date">NAV Date</Label>
          <Input id="nav_date" type="date" {...register("nav_date")} />
        </div>
      </div>

      {/* ── Notes ── */}
      <div className="space-y-1">
        <Label htmlFor="notes">Notes</Label>
        <Textarea
          id="notes"
          {...register("notes")}
          rows={2}
          placeholder="Optional notes"
        />
      </div>

      <DialogFooter className="pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={submitting}>
          Cancel
        </Button>
        <Button type="submit" disabled={submitting}>
          {submitting ? "Saving…" : isEditing ? "Update Fund" : "Add Fund"}
        </Button>
      </DialogFooter>
    </form>
  );
}
